import { ReactElement } from 'react';
import { Controller, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FULL_DAY, SchedulePermission } from '../../schedules';
import { ModalShell } from './ModalShell';
import { PermissionField } from './PermissionField';
import { validateEndTime, validateStartTime } from './TimeInput24h';
import { TimeRangeFields } from './TimeRangeFields';
import {
  BackButton, ModalBody, ModalButtons, ModalError, ModalLabel, ModalSubtitle, ModalTitle,
  SubmitButton, TextInput,
} from './styles';

export interface ExceptionModalValues {
  /** AAAA-MM-DD, o formato do campo de data e da chave da exceção. */
  date: string;
  start: string;
  end: string;
  fullDay: boolean;
  permission: SchedulePermission;
}

export const EMPTY_EXCEPTION_VALUES: ExceptionModalValues = {
  date: '', start: '', end: '', fullDay: false, permission: 'allow',
};

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

interface ExceptionModalProps {
  initialValues: ExceptionModalValues;
  isEdit: boolean;
  onClose: () => void;
  onSubmit: (values: ExceptionModalValues) => void;
}

export function ExceptionModal({
  initialValues, isEdit, onClose, onSubmit,
}: Readonly<ExceptionModalProps>): ReactElement {
  const { t } = useTranslation();
  const {
    control, register, handleSubmit, watch, formState: { errors },
  } = useForm<ExceptionModalValues>({ defaultValues: initialValues });
  const fullDay = watch('fullDay');
  const { field: startField } = useController({
    control, name: 'start', rules: { validate: (value, values) => validateStartTime(value, values.fullDay, t) },
  });
  const { field: endField } = useController({
    control, name: 'end', rules: { validate: (value, values) => validateEndTime(value, values.start, values.fullDay, t) },
  });

  function submit(values: ExceptionModalValues): void {
    onSubmit(values.fullDay ? { ...values, start: FULL_DAY.start, end: FULL_DAY.end } : values);
  }

  return (
    <ModalShell width={520} onClose={onClose}>
      <ModalBody onSubmit={handleSubmit(submit)} noValidate data-testid="eco-simplificado-modal-excecao">
        <ModalTitle>{t(isEdit ? 'ecoSimplificadoEditarExcecao' : 'ecoSimplificadoAdicionarExcecaoTitulo')}</ModalTitle>
        <ModalSubtitle>{t('ecoSimplificadoExcecaoSubtitulo')}</ModalSubtitle>

        <ModalLabel htmlFor="eco-simplificado-dia">{t('dia')}</ModalLabel>
        <TextInput
          id="eco-simplificado-dia"
          type="date"
          min={todayIso()}
          {...register('date', {
            validate: (value) => {
              if (!value) return t('obrigatorio');
              return value >= todayIso() || value === initialValues.date || t('dataDeveSerFutura');
            },
          })}
        />
        {errors.date ? <ModalError>{errors.date.message}</ModalError> : null}

        <TimeRangeFields
          start={{
            id: 'eco-simplificado-de', label: t('ecoSimplificadoDe'), field: startField, error: errors.start?.message,
          }}
          end={{
            id: 'eco-simplificado-ate', label: t('ecoSimplificadoAte'), field: endField, error: errors.end?.message,
          }}
          fullDay={fullDay}
          fullDayLabel={t('ecoSimplificado24Horas')}
          fullDayInput={register('fullDay')}
        />

        <Controller
          control={control}
          name="permission"
          render={({ field }) => <PermissionField value={field.value} onChange={field.onChange} />}
        />

        <ModalButtons>
          <BackButton type="button" onClick={onClose}>{t('botaoVoltar')}</BackButton>
          <SubmitButton type="submit">{t(isEdit ? 'ecoSimplificadoSalvar' : 'botaoAdicionar')}</SubmitButton>
        </ModalButtons>
      </ModalBody>
    </ModalShell>
  );
}
